import {
  Accordion,
  AccordionContent,
  AccordionItem
} from '@components/ui/accordion';
import { Badge } from '@components/ui/badge';
import { SettingsContainer, SettingsContent } from '@components/ui/settings';
import useProcess from '@hooks/useProcess';
import useStores from '@hooks/useStore';
import { LogViewer } from '@patternfly/react-log-viewer';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { ChevronDown, GitCommitHorizontal, Loader2 } from 'lucide-react';
import { observer } from 'mobx-react';
import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useGeneration } from '@/hooks/queries/generations/useGeneration';
import { useGenerationLogs } from '@/hooks/queries/generations/useGenerationLogs';
import { VersionBadge } from '@/pages/Workspaces/components/VersionBadge';
import { ParamsList } from '@/routes/routes.types';
import { ENV, ENV_TO_READABLE_NAME } from '@/utils/constants';
import {
  formatDurationLong,
  formatElapsedTime,
  generationStatusToProgress,
  getSourceTag
} from '..';
import {
  LogViewerSearchInput,
  LogViewerSearchSync
} from './LogViewerSearchInput';

const formatLogsForDisplay = (
  logs: { timestamp: string; level: string; message: string }[]
): string => {
  if (logs.length === 0) {
    return '';
  }

  return logs
    .map(
      log => `[${log.timestamp}] [${log.level.toUpperCase()}] ${log.message}`
    )
    .join('\n');
};

export const GenerationDetails = observer(() => {
  const process = useProcess();
  const { workspaceStore } = useStores();
  const generationId = useParams<string>()[ParamsList.GenerationId];
  const processId = process?.id ?? '';

  const { generation, isLoading, isError, refetch } = useGeneration({
    processId,
    generationId: generationId ?? '',
    enabled: Boolean(processId && generationId)
  });

  const isRunning =
    generation?.status === 'PENDING' || generation?.status === 'RUNNING';

  const {
    logs,
    isError: isLogsError,
    refetch: refetchLogs
  } = useGenerationLogs({
    processId,
    generationId: generationId ?? '',
    enabled: Boolean(processId && generationId),
    isRunning
  });

  const [accordionValue, setAccordionValue] = useState<string>('');
  const [scrollToRow, setScrollToRow] = useState<number | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState('');

  const logLines = useMemo(() => {
    const formatted = formatLogsForDisplay(logs);
    if (!formatted) {
      return [];
    }
    return formatted.split('\n');
  }, [logs]);

  if (!generationId) {
    return <div>Generation ID not found</div>;
  }

  if (!process || process.isSmallProcess || isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-3 text-sm">
        <div className="font-medium text-zinc-700">
          Couldn't load this generation
        </div>
        <button
          className="rounded-md border border-black/[0.08] bg-white px-3 py-1.5 text-zinc-700 hover:bg-gray-50"
          onClick={() => refetch()}
          type="button"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!generation) {
    return <div>Generation not found</div>;
  }

  const durationDisplay = formatDurationLong(generation.duration);
  const sourceTag = getSourceTag(generation.source);
  const workspaceColor = generation.workspaceId
    ? workspaceStore
        .getWorkspacesForProcess(processId)
        .find(w => w.id === generation.workspaceId)?.color
    : undefined;

  return (
    <SettingsContainer>
      <SettingsContent>
        <div className="mt-4 mb-11 rounded-md border border-black/[0.08] bg-white p-5 text-sm shadow-sm">
          <div className="mb-5 flex">
            <div className="flex-1">
              <div className="flex flex-col leading-6">
                <div className="text-zinc-500">Generation ID</div>
                <div className="font-medium text-zinc-950">{generation.id}</div>
              </div>
            </div>
            <div className="flex-1">
              <div className="flex flex-col leading-6">
                <div className="text-zinc-500">Status</div>
                <div className="flex flex-row items-center">
                  {generationStatusToProgress(generation.status)}
                  <div className="ml-2 font-medium text-zinc-950">
                    {generation.status}
                  </div>
                </div>
              </div>
            </div>
            <div className="flex-1">
              <div className="flex flex-col leading-6">
                <div className="text-zinc-500">Deployed to</div>
                <div className="flex flex-row items-center font-medium text-zinc-950">
                  {ENV_TO_READABLE_NAME[ENV]}
                  {generation.isCurrentlyDeployed && (
                    <Badge className="ml-2 font-normal" variant={'pastelBlue'}>
                      Current
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="mb-5 flex">
            <div className="flex-1">
              <div className="flex flex-col leading-6">
                <div className="text-zinc-500">Deployed by</div>
                <div className="flex flex-row items-center font-medium text-zinc-950">
                  {generation.createdBy.name}
                </div>
              </div>
            </div>
            <div className="flex-1">
              <div className="flex flex-col leading-6">
                <div className="text-zinc-500">Triggered at</div>
                <div className="flex flex-row items-center font-medium text-zinc-950">
                  {new Intl.DateTimeFormat('en-US', {
                    dateStyle: 'medium',
                    timeStyle: 'short'
                  }).format(new Date(generation.createdAt))}
                </div>
              </div>
            </div>
            <div className="flex-1">
              <div className="flex flex-col leading-6">
                <div className="text-zinc-500">Duration</div>
                <div className="flex flex-row items-center font-medium text-zinc-950">
                  {durationDisplay} ({formatElapsedTime(generation.createdAt)})
                </div>
              </div>
            </div>
          </div>

          <div className="mb-5 flex">
            <div className="flex-1">
              <div className="flex flex-col leading-6">
                <div className="text-zinc-500">Source</div>
                <div className="flex flex-row items-center">
                  <GitCommitHorizontal size={18} />
                  <div className="ml-2 font-medium text-zinc-950">
                    {sourceTag}
                  </div>
                </div>
              </div>
            </div>
            <div className="flex-1">
              <div className="flex flex-col leading-6">
                <div className="text-zinc-500">Workspace</div>
                {generation.workspaceId ? (
                  <Link
                    className="inline-flex w-fit items-center gap-1.5 rounded-md px-2 py-0.5 font-medium text-zinc-950 shadow-sm ring-1 ring-black/5 transition-colors hover:bg-zinc-50"
                    to={`/${processId}/workspaces/${generation.workspaceId}`}
                  >
                    <span
                      className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                      style={{
                        backgroundColor: workspaceColor || '#6366f1'
                      }}
                    />
                    {generation.workspaceName ?? 'Unnamed workspace'}
                  </Link>
                ) : (
                  <div className="flex flex-row items-center font-medium text-zinc-950">
                    <span className="font-normal text-zinc-400">
                      No workspace
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="flex-1">
              <div className="flex flex-col leading-6">
                <div className="text-zinc-500">Version</div>
                <div className="flex flex-row items-center gap-2 font-medium text-zinc-950">
                  <VersionBadge version={generation.workspaceVersion} />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="-mx-144 mb-10 border-zinc-200 border-b border-solid" />

        <h1 className="mb-6 font-medium">Generation details</h1>

        <Accordion
          collapsible
          onValueChange={setAccordionValue}
          type="single"
          value={accordionValue}
        >
          <AccordionItem
            className="rounded-md border border-zinc-300 border-solid bg-white bg-clip-border font-normal text-sm shadow-md"
            value="item-1"
          >
            <AccordionPrimitive.Header className="flex">
              <AccordionPrimitive.Trigger className="flex flex-1 items-center justify-between px-4 py-4 font-medium transition-all [&[data-state=open]>svg]:rotate-180">
                <div className="flex flex-1 items-center">
                  <div className="mr-2">Logs</div>
                  {generationStatusToProgress(generation.status)}
                </div>
                <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200" />
              </AccordionPrimitive.Trigger>
              <LogViewerSearchInput
                lines={logLines}
                onExpand={() => setAccordionValue('item-1')}
                onScrollToRow={setScrollToRow}
                onSearchChange={setSearchTerm}
              />
            </AccordionPrimitive.Header>
            <AccordionContent className="h-72 rounded-b-md border-zinc-300 border-t border-solid bg-zinc-50 pl-1 font-mono">
              {isLogsError ? (
                <div className="flex h-full flex-col items-center justify-center gap-2 text-sm">
                  <div className="text-zinc-700">Couldn't load logs</div>
                  <button
                    className="rounded-md border border-zinc-300 bg-white px-3 py-1.5 text-zinc-700 hover:bg-gray-50"
                    onClick={() => refetchLogs()}
                    type="button"
                  >
                    Retry
                  </button>
                </div>
              ) : (
                <LogViewer
                  data={formatLogsForDisplay(logs)}
                  hasLineNumbers={true}
                  height="100%"
                  scrollToRow={scrollToRow}
                  toolbar={<LogViewerSearchSync searchTerm={searchTerm} />}
                />
              )}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </SettingsContent>
    </SettingsContainer>
  );
});
